import { useCallback, useEffect, useState } from 'react'
import {
  ServiceFeeDetailsRecord,
  StaffUpdateServiceFeeInfoForCampaignRequest,
  StaffUpdateServiceFeeInfoForCampaignRequestChangeType,
  useInvalidateUseStaffGetCommissionInfoForCampaign,
  useStaffUpdateServiceFeeInfoForCampaign,
} from '@urbanx/agx.campaigns.client'
import {
  AgxButton,
  AgxModal,
  AgxTypography,
  Icon_FeaturedIcon,
  AgxTextInput,
  AgxTable,
  Icon_PartnershipLevelIcon,
  AgxSpacer,
} from '@urbanx/agx.ui-components2'
import EditServiceFeeReason, { ChangeDetails } from './EditServiceFeeReason'
import { useSelectedCampaign } from 'hooks'
import { parseNumber } from 'utils/parseNumber'
import { PartnershipDetailsPartnershipLevel } from '@urbanx/agx.agencies.client'

enum EditServiceFeeModalPage {
  Reason = 'Reason',
  Details = 'Details',
  UpdateSuccess = 'UpdateSuccess',
}

const EditServiceFeeModal = ({
  partnershipLevel,
  onClose,
  currentServiceFeeDetails,
}: {
  partnershipLevel?: PartnershipDetailsPartnershipLevel
  onClose: () => void
  currentServiceFeeDetails: ServiceFeeDetailsRecord
}) => {
  const [currentPage, setCurrentPage] = useState<EditServiceFeeModalPage>(
    EditServiceFeeModalPage.Reason
  )

  const [reasonDetails, setReasonDetails] = useState<ChangeDetails | undefined>(
    undefined
  )
  const [serviceFeeDetails, setServiceFeeDetails] =
    useState<ServiceFeeDetailsRecord>(() => currentServiceFeeDetails)

  const [reasonDetailsValid, setReasonDetailsValid] = useState(false)

  useEffect(() => {
    if (!reasonDetails) setReasonDetailsValid(false)
    else if (
      reasonDetails?.reasonType ===
        StaffUpdateServiceFeeInfoForCampaignRequestChangeType.Other &&
      !reasonDetails?.reason
    )
      setReasonDetailsValid(false)
    else setReasonDetailsValid(true)
  }, [reasonDetails?.reason, reasonDetails?.reasonType, setReasonDetailsValid])

  const invalidateServiceFeeInfo =
    useInvalidateUseStaffGetCommissionInfoForCampaign()

  const { campaign: selectedCampaign } = useSelectedCampaign()

  const {
    staffUpdateServiceFeeInfoForCampaign,
    staffUpdateServiceFeeInfoForCampaignPending,
  } = useStaffUpdateServiceFeeInfoForCampaign({
    onSuccess: async () => {
      await invalidateServiceFeeInfo(selectedCampaign?.campaignId || '')
      setCurrentPage(EditServiceFeeModalPage.UpdateSuccess)
    },
    onError: (error) => {
      console.error('Error updating ServiceFee info:', error)
    },
  })

  useEffect(() => {
    if (currentPage === EditServiceFeeModalPage.UpdateSuccess) {
      const timer = setTimeout(() => {
        onClose()
        setCurrentPage(EditServiceFeeModalPage.Reason)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [currentPage])

  const onReasonDetailsUpdated = useCallback(
    (updatedDetails: ChangeDetails) => {
      setReasonDetails(updatedDetails)
    },
    [setReasonDetails]
  )

  return (
    <AgxModal
      type={`${currentPage === EditServiceFeeModalPage.UpdateSuccess ? 'dialog' : 'single'}`}
      minWidth="600px"
      maxWidth="600px"
      minHeight={
        currentPage === EditServiceFeeModalPage.UpdateSuccess
          ? '200px'
          : undefined
      }
      maxHeight={
        currentPage === EditServiceFeeModalPage.UpdateSuccess
          ? '200px'
          : undefined
      }
      title={
        currentPage === EditServiceFeeModalPage.UpdateSuccess
          ? ''
          : currentPage === EditServiceFeeModalPage.Reason
            ? 'Provide reason for Service Fee update'
            : 'Edit Service Fee'
      }
      variant={
        currentPage === EditServiceFeeModalPage.UpdateSuccess
          ? 'withoutDivider'
          : 'withDivider'
      }
      actionButtonContainerStyle={'withoutDivider'}
      content={
        <AgxSpacer
          gap="none"
          fill
          padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}
        >
          {currentPage === EditServiceFeeModalPage.Reason && (
            <EditServiceFeeReason
              defaultChangeDetails={reasonDetails}
              onChange={onReasonDetailsUpdated}
            />
          )}
          {currentPage === EditServiceFeeModalPage.Details && (
            <AgxTable
              variant="minimal"
              columns={[
                {
                  name: 'Partnership Tier',
                },
                {
                  name: 'UX Service Fee',
                },
              ]}
              tableStyle="borderless"
              rows={[
                {
                  noHover: true,
                  'Partnership Tier': (
                    <AgxSpacer
                      direction="horizontal"
                      contentAlignment="start"
                      contentJustification="start"
                      gap="2xs"
                      fill
                    >
                      <Icon_PartnershipLevelIcon
                        partnershipLevel={
                          partnershipLevel ||
                          PartnershipDetailsPartnershipLevel.Bronze
                        }
                        color="var(--text-primary)"
                        height={16}
                        width={16}
                      />
                      <AgxTypography
                        id={`type`}
                        type={'body_ui'}
                        size={'small'}
                        color="var(--text-primary)"
                      >
                        {partnershipLevel ||
                          PartnershipDetailsPartnershipLevel.Bronze}
                      </AgxTypography>
                    </AgxSpacer>
                  ),
                  'UX Service Fee': (
                    <AgxSpacer
                      direction="horizontal"
                      contentAlignment="start"
                      contentJustification="start"
                      fill
                    >
                      <AgxTextInput
                        id={'ServiceFeePercentage'}
                        onInputValueChange={({ value }: { value: string }) =>
                          setServiceFeeDetails((prev) => {
                            return {
                              ...prev,
                              serviceFeePercentage: parseNumber(value),
                            }
                          })
                        }
                        type="percent"
                        hideOptionalText
                        defaultValue={serviceFeeDetails.serviceFeePercentage?.toString()}
                        errorText={
                          serviceFeeDetails.serviceFeePercentage == null ||
                          serviceFeeDetails.serviceFeePercentage < 0
                            ? 'Enter service fee'
                            : undefined
                        }
                      />
                    </AgxSpacer>
                  ),
                },
              ]}
            />
          )}
          {currentPage === EditServiceFeeModalPage.UpdateSuccess && (
            <AgxSpacer
              gap={'spacing-lg'}
              contentAlignment="center"
              contentJustification="center"
              fill
            >
              <Icon_FeaturedIcon />
              <AgxTypography
                id="ServiceFeeUpdateSuccessMessage"
                type="body_ui_strong"
                size="medium"
                color="var(--text-primary)"
              >
                ServiceFee updated successfully!
              </AgxTypography>
            </AgxSpacer>
          )}
        </AgxSpacer>
      }
      setIsDialogOpen={onClose}
      actionButtons={
        <>
          {currentPage === EditServiceFeeModalPage.Reason && (
            <AgxSpacer
              direction="horizontal"
              gap="spacing-lg"
              fill
              contentJustification="center"
            >
              <AgxButton
                id="ServiceFeeReasonCancel"
                text="Cancel"
                variant="hollow"
                onClick={onClose}
                size="large"
                stretch
              />
              <AgxButton
                id="ServiceFeeReasonNext"
                text="Next"
                variant="primary"
                onClick={() => setCurrentPage(EditServiceFeeModalPage.Details)}
                disabled={!reasonDetailsValid}
                size="large"
                stretch
              />
            </AgxSpacer>
          )}
          {currentPage === EditServiceFeeModalPage.Details && (
            <AgxSpacer
              direction="horizontal"
              gap="spacing-lg"
              fill
              contentJustification="center"
            >
              <AgxButton
                id="ServiceFeeDetailsBack"
                text="Back"
                variant="hollow"
                onClick={() => setCurrentPage(EditServiceFeeModalPage.Reason)}
                size="large"
                stretch
                disabled={staffUpdateServiceFeeInfoForCampaignPending}
              />
              <AgxButton
                id="ServiceFeeDetailsSave"
                text="Save"
                variant="primary"
                onClick={async () => {
                  if (
                    !serviceFeeDetails ||
                    !reasonDetails?.reasonType ||
                    serviceFeeDetails.serviceFeePercentage == null ||
                    serviceFeeDetails.serviceFeePercentage < 0
                  ) {
                    return
                  }
                  await staffUpdateServiceFeeInfoForCampaign({
                    campaignId: selectedCampaign?.campaignId || '',
                    serviceFeePercentage:
                      serviceFeeDetails.serviceFeePercentage,
                    changeType: reasonDetails.reasonType,
                    reason: reasonDetails.reason,
                  } as StaffUpdateServiceFeeInfoForCampaignRequest)
                }}
                disabled={
                  !serviceFeeDetails ||
                  !reasonDetailsValid ||
                  staffUpdateServiceFeeInfoForCampaignPending ||
                  serviceFeeDetails.serviceFeePercentage == null ||
                  serviceFeeDetails.serviceFeePercentage < 0
                }
                size="large"
                stretch
              />
            </AgxSpacer>
          )}
        </>
      }
    />
  )
}

export default EditServiceFeeModal
